"use client";

import { useState, useCallback } from "react";
import FileUploader from "./FileUploader";
import PdfThumbnailGrid from "./PdfThumbnailGrid";
import ProcessingOverlay from "./ProcessingOverlay";
import DownloadResult from "./DownloadResult";
import SignupGateModal from "./SignupGateModal";
import UsageLimitBanner from "./UsageLimitBanner";
import { useToolFetch } from "@/hooks/useToolFetch";

export default function RotatePdfTool() {
  const [file, setFile] = useState<File | null>(null);
  const [rotations, setRotations] = useState<Record<number, number>>({});
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<{ url: string; size: number } | null>(null);
  const [error, setError] = useState("");
  const { toolFetch, quotaError } = useToolFetch();

  const handleFilesSelected = useCallback((files: File[]) => {
    setFile(files[0] || null);
    setRotations({});
    setResult(null);
    setError("");
  }, []);

  const rotate = useCallback(async () => {
    if (!file) return;
    const hasRotations = Object.values(rotations).some((r) => r !== 0);
    if (!hasRotations) {
      setError("Rotate at least one page");
      return;
    }
    setProcessing(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("rotations", JSON.stringify(rotations));

      const res = await toolFetch("/api/tools/rotate", { method: "POST", body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to rotate PDF");
      }

      const blob = await res.blob();
      setResult({ url: URL.createObjectURL(blob), size: blob.size });
    } catch (e: any) {
      setError(e.message || "Failed to rotate PDF");
    } finally {
      setProcessing(false);
    }
  }, [file, rotations]);

  const reset = useCallback(() => {
    setFile(null);
    setRotations({});
    setResult(null);
    setError("");
  }, []);

  if (result) {
    return <DownloadResult fileName="rotated.pdf" fileSize={result.size} downloadUrl={result.url} onReset={reset} />;
  }

  if (quotaError?.gate === "signup") return <SignupGateModal quotaError={quotaError} />;

  return (
    <div className="relative space-y-6">
      {processing && <ProcessingOverlay message="Rotating pages..." />}
      {quotaError?.gate === "limit" && <UsageLimitBanner quotaError={quotaError} />}

      {!file ? (
        <FileUploader accept=".pdf" onFilesSelected={handleFilesSelected} label="Drop your PDF here" description="Click pages to rotate them" />
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">Click a page to rotate 90°</p>
            <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600">
              Change file
            </button>
          </div>

          <PdfThumbnailGrid
            file={file}
            mode="rotate"
            rotations={rotations}
            onRotationsChange={setRotations}
          />

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button onClick={rotate} disabled={processing} className="btn-primary w-full py-3">
            Apply rotation
          </button>
        </>
      )}
    </div>
  );
}
